import React, { useState } from "react";
import { Box, Grid, Typography, TextField, Slider } from "@material-ui/core";

export default function DeepResearchControls({
  minBulletPointsSlide,
  setMinBulletPointsSlide,
  maxBulletPointsSlide,
  setMaxBulletPointsSlide,
  maxWordsBulletSlide,
  setMaxWordsBulletSlide,
  maxRows,
  setMaxRows,
  maxColumns,
  setMaxColumns,
  maxWordsPerCell,
  setMaxWordsPerCell,
  maxWordsTableSlide,
  setMaxWordsTableSlide,
  maxWordsSpeakerNotes,
  setMaxWordsSpeakerNotes,
}) {
  // Error states
  const [maxWordsBulletSlideError, setMaxWordsBulletSlideError] = useState("");
  const [maxRowsError, setMaxRowsError] = useState("");
  const [maxColumnsError, setMaxColumnsError] = useState("");
  const [maxWordsPerCellError, setMaxWordsPerCellError] = useState("");
  const [maxWordsTableSlideError, setMaxWordsTableSlideError] = useState("");
  const [maxWordsSpeakerNotesError, setMaxWordsSpeakerNotesError] = useState(
    ""
  );

  // Validation handlers
  const handleMaxWordsBulletSlideChange = (value) => {
    const numValue = Number(value);
    setMaxWordsBulletSlide(numValue);
    if (numValue < 30 || numValue > 250) {
      setMaxWordsBulletSlideError("Must be between 30 and 250");
    } else {
      setMaxWordsBulletSlideError("");
    }
  };

  const handleMaxRowsChange = (value) => {
    const numValue = Number(value);
    setMaxRows(numValue);
    if (numValue < 1 || numValue > 5) {
      setMaxRowsError("Must be between 1 and 5");
    } else {
      setMaxRowsError("");
    }
  };

  const handleMaxColumnsChange = (value) => {
    const numValue = Number(value);
    setMaxColumns(numValue);
    if (numValue < 1 || numValue > 5) {
      setMaxColumnsError("Must be between 1 and 5");
    } else {
      setMaxColumnsError("");
    }
  };

  const handleMaxWordsPerCellChange = (value) => {
    const numValue = Number(value);
    setMaxWordsPerCell(numValue);
    if (numValue < 1 || numValue > 10) {
      setMaxWordsPerCellError("Must be between 1 and 10");
    } else {
      setMaxWordsPerCellError("");
    }
  };

  const handleMaxWordsTableSlideChange = (value) => {
    const numValue = Number(value);
    setMaxWordsTableSlide(numValue);
    if (numValue < 1 || numValue > 250) {
      setMaxWordsTableSlideError("Must be between 1 and 250");
    } else {
      setMaxWordsTableSlideError("");
    }
  };

  const handleMaxWordsSpeakerNotesChange = (value) => {
    const numValue = Number(value);
    setMaxWordsSpeakerNotes(numValue);
    if (numValue < 30 || numValue > 300) {
      setMaxWordsSpeakerNotesError("Must be between 30 and 300");
    } else {
      setMaxWordsSpeakerNotesError("");
    }
  };

  return (
    <Box ml={4} mt={2}>
      <Typography
        variant="subtitle2"
        gutterBottom
        style={{ fontWeight: 600, marginBottom: 16 }}
      >
        Content Generation Controls
      </Typography>

      {/* Bullet Slide Controls */}
      <Typography
        variant="body2"
        style={{ fontWeight: 600, marginTop: 16, marginBottom: 8 }}
      >
        Bullet Slide Settings
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" gutterBottom>
            Bullet points per slide: {minBulletPointsSlide} -{" "}
            {maxBulletPointsSlide}
          </Typography>
          <Slider
            value={[minBulletPointsSlide, maxBulletPointsSlide]}
            onChange={(e, newValue) => {
              setMinBulletPointsSlide(newValue[0]);
              setMaxBulletPointsSlide(newValue[1]);
            }}
            valueLabelDisplay="auto"
            min={1}
            max={10}
            marks={[
              { value: 1, label: "1" },
              { value: 10, label: "10" },
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Max words per slide"
            type="number"
            value={maxWordsBulletSlide}
            onChange={(e) => handleMaxWordsBulletSlideChange(e.target.value)}
            inputProps={{ min: 30, max: 250 }}
            fullWidth
            variant="outlined"
            size="small"
            error={!!maxWordsBulletSlideError}
            helperText={maxWordsBulletSlideError}
          />
        </Grid>
      </Grid>

      {/* Table Slide Controls */}
      <Typography
        variant="body2"
        style={{ fontWeight: 600, marginTop: 16, marginBottom: 8 }}
      >
        Table Slide Settings
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Max rows"
            type="number"
            value={maxRows}
            onChange={(e) => handleMaxRowsChange(e.target.value)}
            inputProps={{ min: 1, max: 5 }}
            fullWidth
            variant="outlined"
            size="small"
            error={!!maxRowsError}
            helperText={maxRowsError}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Max columns"
            type="number"
            value={maxColumns}
            onChange={(e) => handleMaxColumnsChange(e.target.value)}
            inputProps={{ min: 1, max: 5 }}
            fullWidth
            variant="outlined"
            size="small"
            error={!!maxColumnsError}
            helperText={maxColumnsError}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Max words/cell"
            type="number"
            value={maxWordsPerCell}
            onChange={(e) => handleMaxWordsPerCellChange(e.target.value)}
            inputProps={{ min: 2, max: 7 }}
            fullWidth
            variant="outlined"
            size="small"
            error={!!maxWordsPerCellError}
            helperText={maxWordsPerCellError}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Max words/table slide"
            type="number"
            value={maxWordsTableSlide}
            onChange={(e) => handleMaxWordsTableSlideChange(e.target.value)}
            inputProps={{ min: 30, max: 250 }}
            fullWidth
            variant="outlined"
            size="small"
            error={!!maxWordsTableSlideError}
            helperText={maxWordsTableSlideError}
          />
        </Grid>
      </Grid>

      {/* Speaker Notes Controls */}
      <Typography
        variant="body2"
        style={{ fontWeight: 600, marginTop: 16, marginBottom: 8 }}
      >
        Speaker Notes Settings
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Max words in speaker notes per slide"
            type="number"
            value={maxWordsSpeakerNotes}
            onChange={(e) => handleMaxWordsSpeakerNotesChange(e.target.value)}
            inputProps={{ min: 30, max: 300 }}
            fullWidth
            variant="outlined"
            size="small"
            error={!!maxWordsSpeakerNotesError}
            helperText={maxWordsSpeakerNotesError}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
